import { createHash } from "node:crypto";
import { readFileSync } from "node:fs";
import { fileURLToPath } from "node:url";
import { dirname, join } from "node:path";
import { Decimal } from "../core/models.js";
import type { PricingSource } from "../core/models.js";

// Sprint 3 Theme E / §4.2.3 — Node 18 compat: runtime JSON load.
const _thisDir = dirname(fileURLToPath(import.meta.url));
const costMapData = JSON.parse(
  readFileSync(join(_thisDir, "cost_map.json"), "utf-8"),
);

export interface CostResult {
  /** Exact decimal cost. Trailing-zero-stripped, never float64. */
  costUsd: Decimal;
  pricingSource: PricingSource;
  costConfidence: "computed" | "unknown";
  pricingVersion: string;
}

interface ModelPricing {
  input_cost_per_token: number;
  output_cost_per_token: number;
  cache_read_input_token_cost?: number;
  cache_creation_input_token_cost?: number;
  litellm_provider?: string;
}

interface CustomPricing {
  inputPer1k: number;
  outputPer1k: number;
}

/**
 * Coerce a numeric rate/literal to Decimal via String() so a float64 rate
 * from the JSON cost map never poisons the product. Mirrors Python's
 * `Decimal(str(rate))`.
 */
function dec(v: number): Decimal {
  return new Decimal(String(v));
}

/** Anthropic reports input, cache-read, and cache-write as disjoint buckets. */
function usesDisjointCacheBuckets(model: string, provider: string = ""): boolean {
  provider = provider.toLowerCase();
  const normalizedModel = model.toLowerCase();
  return provider === "anthropic"
    || provider === "vertex_ai-anthropic_models"
    || normalizedModel.includes("claude")
    || normalizedModel.includes("anthropic.");
}

export class PricingEngine {
  private _modelMap: Record<string, ModelPricing>;
  private _customPricing: Map<string, CustomPricing> = new Map();
  private _pricingVersion: string;
  private _refreshInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    try {
      this._modelMap = costMapData as Record<string, ModelPricing>;
      this._pricingVersion = createHash("sha256")
        .update(JSON.stringify(costMapData))
        .digest("hex")
        .slice(0, 12);
    } catch {
      this._modelMap = {};
      this._pricingVersion = "unknown";
    }
  }

  get pricingVersion(): string {
    return this._pricingVersion;
  }

  getCost(
    model: string,
    inputTokens: number,
    outputTokens: number,
    cachedTokens: number = 0,
    cacheCreationTokens: number = 0
  ): CostResult {
    const custom = this._customPricing.get(model);
    if (custom) {
      // Decimal math throughout — rates routed through String() so a float64
      // rate literal never poisons the product (matches Python Decimal(str)).
      const safeInput = Math.max(0, inputTokens);
      const safeOutput = Math.max(0, outputTokens);
      const safeCached = Math.max(0, cachedTokens);
      const safeCreation = Math.max(0, cacheCreationTokens);
      const hasUnpricedDisjointCache = usesDisjointCacheBuckets(model)
        && (safeCached > 0 || safeCreation > 0);
      const billableInput = safeInput
        + (hasUnpricedDisjointCache ? safeCached + safeCreation : 0);
      const cost = dec(custom.inputPer1k)
        .times(billableInput)
        .dividedBy(1000)
        .plus(dec(custom.outputPer1k).times(safeOutput).dividedBy(1000));
      return {
        costUsd: cost,
        costConfidence: hasUnpricedDisjointCache ? "unknown" : "computed",
        pricingSource: "custom",
        pricingVersion: this._pricingVersion,
      };
    }

    const info = this._resolveModel(model);
    if (!info) {
      return {
        costUsd: new Decimal(0),
        costConfidence: "unknown",
        pricingSource: "unknown",
        pricingVersion: this._pricingVersion,
      };
    }

    const inputRate = dec(info.input_cost_per_token);
    const outputRate = dec(info.output_cost_per_token);
    const hasCacheReadRate = info.cache_read_input_token_cost !== undefined;
    const hasCacheCreationRate = info.cache_creation_input_token_cost !== undefined;
    const cacheReadRate = dec(info.cache_read_input_token_cost ?? info.input_cost_per_token);
    const cacheCreationRate = dec(
      info.cache_creation_input_token_cost ?? info.input_cost_per_token,
    );
    const safeInput = Math.max(0, inputTokens);
    const safeOutput = Math.max(0, outputTokens);
    const safeCached = Math.max(0, cachedTokens);
    const safeCreation = Math.max(0, cacheCreationTokens);

    let cost: Decimal;
    let costConfidence: "computed" | "unknown" = "computed";
    if (usesDisjointCacheBuckets(model, info.litellm_provider)) {
      // Anthropic usage exposes three independent input buckets. Subtracting
      // cache tokens from input_tokens drops most of the billable usage.
      cost = inputRate
        .times(safeInput)
        .plus(cacheReadRate.times(safeCached))
        .plus(cacheCreationRate.times(safeCreation))
        .plus(outputRate.times(safeOutput));
      if (
        (safeCached > 0 && !hasCacheReadRate)
        || (safeCreation > 0 && !hasCacheCreationRate)
      ) {
        costConfidence = "unknown";
      }
    } else {
      // OpenAI-style usage includes cached tokens inside input_tokens. Only
      // subtract a cache bucket when the catalog supplies its dedicated rate.
      const effectiveCached = hasCacheReadRate
        ? Math.min(safeCached, safeInput)
        : 0;
      const remaining = safeInput - effectiveCached;
      const effectiveCreation = hasCacheCreationRate
        ? Math.min(safeCreation, remaining)
        : 0;
      const nonCachedInput = remaining - effectiveCreation;
      cost = inputRate
        .times(nonCachedInput)
        .plus(cacheReadRate.times(effectiveCached))
        .plus(cacheCreationRate.times(effectiveCreation))
        .plus(outputRate.times(safeOutput));
    }

    return {
      costUsd: cost,
      costConfidence,
      pricingSource: "litellm",
      pricingVersion: this._pricingVersion,
    };
  }

  setCustomPricing(model: string, inputPer1k: number, outputPer1k: number): void {
    this._customPricing.set(model, { inputPer1k, outputPer1k });
  }

  private _apiKey: string | undefined;

  setApiKey(key: string | undefined): void {
    this._apiKey = key;
  }

  async refreshFromServer(endpoint: string): Promise<void> {
    try {
      const headers: Record<string, string> = { "User-Agent": "dexcost-sdk" };
      if (this._apiKey) {
        headers["Authorization"] = `Bearer ${this._apiKey}`;
      }
      const response = await fetch(`${endpoint}/v1/api/pricing-data/latest`, {
        headers,
      });
      if (!response.ok) return;
      const text = await response.text();
      let payload: { data?: { data?: Record<string, ModelPricing>; pricing_version?: string } };
      try {
        payload = JSON.parse(text) as typeof payload;
      } catch {
        return; // Malformed JSON — keep using bundled pricing
      }
      // Control Layer contract: pricing models are nested under
      // payload.data.data, with payload.data.pricing_version alongside.
      const rawData = payload.data;
      if (!rawData || typeof rawData !== "object") return;
      const serverData = rawData.data;
      if (
        !serverData
        || typeof serverData !== "object"
        || Array.isArray(serverData)
        || Object.keys(serverData).length === 0
      ) {
        return;
      }
      // Drop the schema sample if present (matches Python).
      delete (serverData as Record<string, unknown>).sample_spec;
      if (Object.keys(serverData).length === 0) return;
      this._modelMap = serverData;
      this._pricingVersion =
        typeof rawData.pricing_version === "string" && rawData.pricing_version
          ? rawData.pricing_version
          : createHash("sha256")
              .update(JSON.stringify(serverData))
              .digest("hex")
              .slice(0, 12);
    } catch {
      // Network error — keep using bundled pricing
    }
  }

  startBackgroundRefresh(endpoint: string, intervalMs: number = 86_400_000): void {
    void this.refreshFromServer(endpoint);
    const interval = setInterval(
      () => void this.refreshFromServer(endpoint),
      intervalMs
    );
    interval.unref();
    this._refreshInterval = interval;
  }

  stopBackgroundRefresh(): void {
    if (this._refreshInterval !== null) {
      clearInterval(this._refreshInterval);
      this._refreshInterval = null;
    }
  }

  private _resolveModel(model: string): ModelPricing | undefined {
    if (model in this._modelMap) return this._modelMap[model];

    if (model.includes("/")) {
      const short = model.split("/").pop()!;
      if (short in this._modelMap) return this._modelMap[short];
    }

    const parts = model.split("-");
    for (let i = parts.length - 1; i > 0; i--) {
      const candidate = parts.slice(0, i).join("-");
      if (candidate in this._modelMap) return this._modelMap[candidate];
    }

    return undefined;
  }
}
